/**
 * Fetch blog Pages from Contentful for header typeahead: keyword match on Page.title
 * plus blog metadata tags (same pool as BlogListing / search spotlight).
 */

import {print} from 'graphql';

import {useQuery} from '@tanstack/react-query';

import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site';

import {useService} from '@amblique/base/app/hooks/use-service';
import {ContentfulService} from '@amblique/contentful/app/services/contentful-service';
import {pageWhere} from '@amblique/king-living/app/contentful/helpers/region';
import {getBlogListingPages} from '@amblique/king-living/app/contentful/queries/blog-listing-pages.queries';
import {PAGE_BLOG_METADATA_TAG_IDS} from '@amblique/king-living/app/contentful/utils/blog-metadata-tag-ids';

/** Keep typeahead query size bounded for GraphQL and UX. */
const MAX_KEYWORD_LENGTH = 120;

const blogPageTagClause = {
  contentfulMetadata: {
    tags: {
      id_contains_some: PAGE_BLOG_METADATA_TAG_IDS,
    },
  },
};

/**
 * @param {string} keyword - Raw search string (trimmed / capped inside the hook).
 * @param {object} [options={}]
 * @param {number} [options.limit=4] - Max blog pages to return.
 * @param {import('@tanstack/react-query').UseQueryOptions} [options.queryOptions={}]
 */
export function useBlogPagesByKeywordSearch(keyword, options = {}) {
  const contentfulService = useService(ContentfulService);
  const {site} = useMultiSite();
  const normalized = String(keyword || '')
    .trim()
    .slice(0, MAX_KEYWORD_LENGTH);

  const limit = typeof options.limit === 'number' ? options.limit : 4;
  const {queryOptions = {}} = options;
  const {enabled: enabledFromOptions, ...restQueryOptions} = queryOptions;

  const filterClauses = [blogPageTagClause];

  if (normalized.length > 0) {
    filterClauses.push({title_contains: normalized});
  }

  const where = pageWhere(filterClauses, contentfulService?.locale, site?.id);

  const resolvedEnabled =
    !!contentfulService &&
    normalized.length >= 3 &&
    (typeof enabledFromOptions === 'boolean' ? enabledFromOptions : true);

  return useQuery({
    queryKey: [
      contentfulService?.key,
      'blog-pages-keyword-typeahead',
      contentfulService?.locale,
      site?.id,
      normalized,
      limit
    ],

    queryFn: async () => {
      if (!contentfulService) {
        throw new Error('ContentfulService not available');
      }

      const data = await contentfulService.query(print(getBlogListingPages), {
        limit,
        skip: 0,
        locale: contentfulService?.locale,
        preview: false,
        where,
        order: ['sys_publishedAt_DESC'],
      });

      return data.pageCollection || {total: 0, skip: 0, limit: 0, items: []};
    },

    enabled: resolvedEnabled,
    staleTime: 60 * 1000,
    gcTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    ...restQueryOptions,
  });
}
